import React, { useEffect, useRef } from 'react';
import { GlobeHemisphereWest, MapPin } from '@phosphor-icons/react';
import './AlumniSection.css';

// Import university logos
import uinJktLogo from '../assets/images/universities/uin-jkt.png';
import uinMlgLogo from '../assets/images/universities/uin-mlg.png';
import uinYkLogo from '../assets/images/universities/uin-yk.png';
import uiLogo from '../assets/images/universities/ui.png';
import ugmLogo from '../assets/images/universities/ugm.png';
import itbLogo from '../assets/images/universities/itb.png';
import unpadLogo from '../assets/images/universities/unpad.png';
import uiiLogo from '../assets/images/universities/uii.png';
import uimLogo from '../assets/images/universities/uim.png';
import azharLogo from '../assets/images/universities/azhar.png';
import ahqafLogo from '../assets/images/universities/ahqaf.png';
import anadoluLogo from '../assets/images/universities/anadolu.png';

const internationalUniversities = [
  { name: 'Islamic University of Madinah', location: 'Saudi Arabia', logo: uimLogo },
  { name: 'Al-Azhar University', location: 'Egypt', logo: azharLogo },
  { name: 'Al-Ahqaf University', location: 'Yemen', logo: ahqafLogo },
  { name: 'Anadolu University', location: 'Turkey', logo: anadoluLogo },
];

const nationalUniversities = [
  { name: 'Universitas Indonesia', location: 'Indonesia', logo: uiLogo },
  { name: 'Institut Teknologi Bandung', location: 'Indonesia', logo: itbLogo },
  { name: 'Universitas Gadjah Mada', location: 'Indonesia', logo: ugmLogo },
  { name: 'UIN Syarif Hidayatullah', location: 'Indonesia', logo: uinJktLogo },
  { name: 'UIN Sunan Kalijaga', location: 'Indonesia', logo: uinYkLogo },
  { name: 'UIN Maulana Malik Ibrahim', location: 'Indonesia', logo: uinMlgLogo },
  { name: 'Universitas Padjadjaran', location: 'Indonesia', logo: unpadLogo },
  { name: 'Universitas Islam Indonesia', location: 'Indonesia', logo: uiiLogo },
];

const AlumniCard = ({ uni, index }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale(1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="alumni-card stagger-item"
      style={{ animationDelay: `${0.2 + index * 0.1}s` }}
    >
      <div className="alumni-card-bg-shape"></div>
      {/* Logo langsung di card, tanpa wrapper tambahan */}
      <img 
        src={uni.logo} 
        alt={uni.name}
        className="alumni-logo"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <span className="alumni-logo-fallback">
        {uni.name.split(' ').map(n => n[0]).join('')}
      </span>
      <div className="alumni-card-content">
        <p className="alumni-uni-name">{uni.name}</p>
        <p className="alumni-uni-location">
          <MapPin weight="fill" className="location-icon" />
          {uni.location}
        </p>
      </div>
    </div>
  );
};

const AlumniSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.stagger-item, .header-animate');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="alumni-section" ref={sectionRef}>
      <div className="container-tight">
        {/* Section Header */}
        <div className="alumni-header header-animate">
          <div className="alumni-header-grid">
            <div className="alumni-header-left">
              <span className="alumni-label">ALUMNI KAMI</span>
              <h2 className="alumni-title">
                Melanjutkan ke<br/>
                <span className="alumni-title-muted">Perguruan Tinggi Terbaik</span>
              </h2>
            </div>
            <div className="alumni-header-right">
              <p className="alumni-description">
                Alumni Bahrul Ulum telah berhasil melanjutkan pendidikan ke 
                berbagai universitas ternama di Indonesia dan dunia.
              </p>
            </div>
          </div>
        </div>

        {/* International Universities */}
        <div className="alumni-international">
          <div className="alumni-section-header stagger-item">
            <div className="alumni-section-icon">
              <GlobeHemisphereWest weight="fill" />
            </div>
            <h3 className="alumni-section-title">Universitas Internasional</h3>
          </div>
          
          <div className="alumni-logo-grid">
            {internationalUniversities.map((uni, index) => (
              <AlumniCard 
                key={uni.name} 
                uni={uni} 
                index={index}
              />
            ))}
          </div>
        </div>

        {/* National Universities */}
        <div className="alumni-national">
          <div className="alumni-section-header stagger-item">
            <span className="alumni-id-badge">ID</span>
            <h3 className="alumni-section-title">Universitas Nasional</h3>
          </div>
          
          <div className="alumni-logo-grid">
            {nationalUniversities.map((uni, index) => (
              <AlumniCard 
                key={uni.name} 
                uni={uni} 
                index={index + 4}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlumniSection;
